import { useTranslation } from "react-i18next";

import { formatDateTime } from "../../lib/format";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import type { LoginSession } from "./api";
import { describeAgent } from "./routing";

/**
 * A list of login sessions ([DO-013], [NF-008], [AC-063]): when it began,
 * when it ends, when it was last used, from where - and the button that ends
 * it. Shared by the own account (own sessions) and the administration (an
 * account's sessions); the caller hands the table its handle.
 */
export function SessionsTable({
  testid,
  sessions,
  busyId,
  onRevoke,
}: {
  testid: string;
  sessions: LoginSession[];
  busyId: string | null;
  onRevoke: (session: LoginSession) => void;
}): JSX.Element {
  const { t } = useTranslation();

  if (sessions.length === 0) {
    return (
      <p data-testid="sessions_none" className="text-sm text-muted-foreground">
        {t("sessions.none")}
      </p>
    );
  }

  return (
    <div className="overflow-x-auto rounded-lg border">
      <Table data-testid={testid}>
        <TableHeader>
          <TableRow>
            <TableHead>{t("sessions.device")}</TableHead>
            <TableHead>{t("sessions.since")}</TableHead>
            <TableHead>{t("sessions.lastSeen")}</TableHead>
            <TableHead>{t("sessions.endsAt")}</TableHead>
            <TableHead className="w-28" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {sessions.map((session) => (
            <TableRow key={session.id} data-testid={`sessions_row_${session.id}`}>
              <TableCell title={session.user_agent}>
                <div className="flex flex-wrap items-center gap-2">
                  <span>{describeAgent(session.user_agent)}</span>
                  {session.current ? (
                    <Badge data-testid={`sessions_current_${session.id}`} variant="secondary">
                      {t("sessions.current")}
                    </Badge>
                  ) : null}
                  {session.elevated_until && new Date(session.elevated_until).getTime() > Date.now() ? (
                    <Badge data-testid={`sessions_elevated_${session.id}`} variant="outline">
                      {t("sessions.elevated")}
                    </Badge>
                  ) : null}
                </div>
                <div className="font-mono text-xs text-muted-foreground">{session.client_ip}</div>
              </TableCell>
              <TableCell className="whitespace-nowrap text-sm">{formatDateTime(session.created_at)}</TableCell>
              <TableCell className="whitespace-nowrap text-sm">{formatDateTime(session.last_seen_at)}</TableCell>
              <TableCell className="whitespace-nowrap text-sm">{formatDateTime(session.expires_at)}</TableCell>
              <TableCell className="text-right">
                <Button
                  data-testid={`sessions_revoke_${session.id}`}
                  variant="outline"
                  size="sm"
                  disabled={busyId === session.id}
                  onClick={() => onRevoke(session)}
                >
                  {t("sessions.revoke")}
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
